import React, { useEffect, useMemo, useState } from "react";

type LogLevel = "info" | "success" | "error";
type DashboardState = Record<string, unknown>;

type DebugLog = {
    id: string;
    at: string;
    level: LogLevel;
    userId: string;
    message: string;
    data?: unknown;
};

type LiveDashBoardProps = {
    gameUuid: string | null;
};

type AgentEnvironment = {
    variables?: { users?: unknown; assignment?: unknown; gameUuid?: unknown; contentId?: unknown };
    auth?: { user?: unknown; info?: { name?: string } };
};

const DEBUG_GAME_UUID_OVERRIDE: string | null = null;
const DEBUG_USER_ID_OVERRIDES: string[] = [];

const readString = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const toRecord = (value: unknown): DashboardState => (value && typeof value === "object" ? (value as DashboardState) : {});
const normalizeUsers = (value: unknown) => (Array.isArray(value) ? value.map((u) => readString(u)).filter(Boolean) : []);

const resolveContentIdFromAssignmentState = (assignmentState: DashboardState) => {
    const candidates = [
        assignmentState.content,
        assignmentState.contentId,
        assignmentState.contentUuid,
        assignmentState.gameUuid,
        assignmentState.game_uuid,
        assignmentState.uuid,
        assignmentState.uuid_gameinfo,
    ];
    for (const candidate of candidates) {
        const text = readString(candidate);
        if (text) return text;
    }
    return "";
};

const summarizeSituationState = (state: DashboardState) => {
    const startedAt = readString(state.startedAt);
    const answersRaw = state.correctAnswers;
    const correctAnswers = Array.isArray(answersRaw) ? answersRaw : [];
    const latest = correctAnswers.length ? toRecord(correctAnswers[correctAnswers.length - 1]) : null;
    return {
        startedAt: startedAt || "-",
        totalCorrect: correctAnswers.length,
        latest: latest
            ? {
                  questionNo: latest.questionNo ?? "-",
                  questionId: latest.questionId ?? "-",
                  answeredAt: latest.answeredAt ?? "-",
                  elapsedMs: latest.elapsedMs ?? "-",
              }
            : null,
    };
};

const LiveDashBoard: React.FC<LiveDashBoardProps> = ({ gameUuid }) => {
    const [logs, setLogs] = useState<DebugLog[]>([]);
    const [latestState, setLatestState] = useState<DashboardState | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const pushLog = (entry: Omit<DebugLog, "id" | "at">) => {
        const now = new Date();
        setLogs((prev) => [
            ...prev,
            {
                ...entry,
                id: `${now.getTime()}-${Math.random().toString(16).slice(2)}`,
                at: now.toLocaleTimeString("th-TH", { hour12: false }),
            },
        ]);
    };

    useEffect(() => {
        let mounted = true;
        const unsubs: Array<() => void> = [];

        const init = async () => {
            setIsLoading(true);
            setLogs([]);
            setLatestState(null);

            try {
                const { default: Agent } = await import("@knowlearning/agents");
                const env = (await Agent.environment()) as AgentEnvironment;
                const assignmentId = readString(env?.variables?.assignment);
                const assignmentState = assignmentId ? toRecord(await Agent.state(assignmentId)) : {};
                const contentFromEnvVars = readString(env?.variables?.gameUuid) || readString(env?.variables?.contentId);
                const contentFromAssignment = resolveContentIdFromAssignmentState(assignmentState);
                const contentFromRowUuid = readString(DEBUG_GAME_UUID_OVERRIDE) || readString(gameUuid);
                // Alignment order: row.uuid -> env.variables(gameUuid/contentId) -> assignment state.
                const contentId = contentFromRowUuid || contentFromEnvVars || contentFromAssignment;
                const key = contentId ? `runstate-${contentId}` : "";

                const usersFromEnv = normalizeUsers(env?.variables?.users);
                const userOverrides = DEBUG_USER_ID_OVERRIDES.map((id) => readString(id)).filter(Boolean);
                const users = [...new Set([...usersFromEnv, ...userOverrides])];

                pushLog({
                    level: "info",
                    userId: "system",
                    message: "Demo-style init",
                    data: {
                        assignmentId,
                        assignmentState,
                        contentFromRowUuid,
                        contentFromEnvVars,
                        contentFromAssignment,
                        inputGameUuid: gameUuid,
                        resolvedContentId: contentId,
                        watchKey: key || null,
                        usersFromEnv,
                        userOverrides,
                        users,
                    },
                });

                if (!key) {
                    pushLog({
                        level: "error",
                        userId: "system",
                        message: "Cannot resolve watch key",
                        data: { assignmentId, assignmentState, inputGameUuid: gameUuid },
                    });
                    return;
                }

                // Always listen to no-user runstate as a fallback because some games
                // write global state even when assignment users exist.
                const noUserInitial = toRecord(await Agent.state(key));
                if (mounted) {
                    if (Object.keys(noUserInitial).length > 0) setLatestState(noUserInitial);
                    pushLog({
                        level: "success",
                        userId: "no-user",
                        message: "Initial state loaded (no-user)",
                        data: summarizeSituationState(noUserInitial),
                    });
                }

                const noUserUnsub = Agent.watch(key, ({ state }: { state: object }) => {
                    if (!mounted) return;
                    const next = toRecord(state);
                    if (Object.keys(next).length > 0) setLatestState(next);
                    pushLog({
                        level: "success",
                        userId: "no-user",
                        message: "State changed (no-user)",
                        data: summarizeSituationState(next),
                    });
                });
                if (typeof noUserUnsub === "function") unsubs.push(noUserUnsub);

                if (!users.length) {
                    return;
                }

                for (const userId of users) {
                    const userEnv = (await Agent.environment(userId)) as AgentEnvironment;
                    const name = readString(userEnv?.auth?.info?.name) || userId;
                    const initial = toRecord(await Agent.state(key, userId));
                    if (mounted) {
                        if (Object.keys(initial).length > 0) setLatestState(initial);
                        pushLog({
                            level: "success",
                            userId,
                            message: `Initial state loaded (${name})`,
                            data: summarizeSituationState(initial),
                        });
                    }

                    const unsub = Agent.watch(
                        key,
                        ({ state }: { state: object }) => {
                            if (!mounted) return;
                            const next = toRecord(state);
                            if (Object.keys(next).length > 0) setLatestState(next);
                            pushLog({
                                level: "success",
                                userId,
                                message: `State changed (${name})`,
                                data: summarizeSituationState(next),
                            });
                        },
                        userId
                    );
                    if (typeof unsub === "function") unsubs.push(unsub);
                }
            } catch (error) {
                pushLog({
                    level: "error",
                    userId: "system",
                    message: "Init failed",
                    data: error instanceof Error ? error.message : error,
                });
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        void init();
        return () => {
            mounted = false;
            unsubs.forEach((unsub) => {
                try {
                    unsub();
                } catch {
                    // ignore
                }
            });
        };
    }, [gameUuid]);

    const header = useMemo(
        () => ({
            inputGameUuid: gameUuid,
            debugGameUuidOverride: DEBUG_GAME_UUID_OVERRIDE,
            debugUserOverrides: DEBUG_USER_ID_OVERRIDES,
            totalLogs: logs.length,
            loading: isLoading,
        }),
        [gameUuid, isLoading, logs.length]
    );

    return (
        <div className="flex h-full w-full flex-col gap-3 bg-[#111827] p-3 text-[#E5E7EB]">
            <div className="rounded border border-[#374151] bg-[#0B1220] p-3">
                <div className="mb-2 text-sm font-semibold text-[#93C5FD]">Live Monitor Debug Header</div>
                <pre className="overflow-x-auto text-xs leading-5">{JSON.stringify(header, null, 2)}</pre>
            </div>

            <div className="rounded border border-[#374151] bg-[#0B1220] p-3">
                <div className="mb-2 text-sm font-semibold text-[#86EFAC]">Latest State Snapshot</div>
                <pre className="max-h-52 overflow-auto text-xs leading-5">{JSON.stringify(latestState, null, 2) || "null"}</pre>
            </div>

            <div className="flex-1 overflow-auto rounded border border-[#374151] bg-[#0B1220] p-3">
                <div className="mb-2 text-sm font-semibold text-[#FCA5A5]">Event Logs</div>
                {logs.length === 0 ? (
                    <div className="text-xs text-gray-400">{isLoading ? "Loading logs..." : "No events yet."}</div>
                ) : (
                    <div className="space-y-2">
                        {logs.map((log) => (
                            <div key={log.id} className="rounded border border-[#1F2937] bg-[#111827] p-2">
                                <div className="mb-1 text-xs">
                                    [{log.at}] [{log.level.toUpperCase()}] [{log.userId}] {log.message}
                                </div>
                                <pre className="overflow-x-auto text-xs leading-5 text-[#D1D5DB]">
                                    {JSON.stringify(log.data ?? null, null, 2)}
                                </pre>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveDashBoard;